<script setup>
import { useI18n } from 'vue-i18n';
import { reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { minLength, required } from '@vuelidate/validators';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Input from 'next/input/Input.vue';
import TextArea from 'next/textarea/TextArea.vue';
import Button from 'next/button/Button.vue';
import SettingsSection from 'components/SettingsSection.vue';

const props = defineProps({
  samlProvider: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const getters = useStoreGetters();
const store = useStore();

const uiFlags = getters['wgptSamlProviders/getUIFlags'];

const state = reactive({
  display_name: props.samlProvider.display_name || '',
  identifier: props.samlProvider.identifier || '',
  sp_metadata: props.samlProvider.sp_metadata || '',
});

const rules = {
  display_name: { required, minLength: minLength(1) },
  identifier: {},
  sp_metadata: { required },
};

const v$ = useVuelidate(rules, state);

const downloadFile = (content, filename) => {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

const onDownloadMetadata = () => {
  if (props.samlProvider.idp_metadata) {
    downloadFile(
      props.samlProvider.idp_metadata,
      `metadata-${props.samlProvider.identifier}.xml`
    );
  }
};

const onDownloadSigningCertificate = () => {
  if (props.samlProvider.idp_signing_cert) {
    downloadFile(
      props.samlProvider.idp_signing_cert,
      `signing-certificate-${props.samlProvider.identifier}.crt`
    );
  }
};

const onDownloadEncryptionCertificate = () => {
  if (props.samlProvider.idp_encryption_cert) {
    downloadFile(
      props.samlProvider.idp_encryption_cert,
      `encryption-certificate-${props.samlProvider.identifier}.crt`
    );
  }
};

const onSubmit = async () => {
  try {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    const data = {
      id: props.samlProvider.id,
      display_name: state.display_name,
      sp_metadata: state.sp_metadata,
    };
    await store.dispatch('wgptSamlProviders/update', data);

    useAlert(t('WGPT_SAML.PROVIDERS.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error.message || t('WGPT_SAML.PROVIDERS.EDIT.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};
</script>

<template>
  <div>
    <div class="max-w-6xl mx-auto">
      <div class="p-8">
        <div class="flex flex-col w-full gap-8">
          <SettingsSection
            :title="t('WGPT_SAML.PROVIDERS.EDIT.BASIC_SETTINGS_TITLE')"
            :sub-title="t('WGPT_SAML.PROVIDERS.EDIT.BASIC_SETTINGS_SUBHEADER')"
          >
            <div class="sm:max-w-screen-md flex flex-col gap-8">
              <div class="flex flex-col gap-1">
                <label
                  class="text-sm font-medium whitespace-nowrap text-slate-900 dark:text-slate-50 text-wrap"
                >
                  {{ t('WGPT_SAML.PROVIDERS.EDIT.FORM.DISPLAY_NAME.LABEL') }}
                </label>
                <div
                  class="relative flex flex-col min-w-0 gap-1"
                  :class="{ error: v$.display_name.$error }"
                >
                  <Input
                    v-model.trim="state.display_name"
                    type="text"
                    :message="
                      v$.display_name.$error
                        ? t('WGPT_SAML.PROVIDERS.EDIT.FORM.DISPLAY_NAME.ERROR')
                        : ''
                    "
                    :message-type="v$.display_name.$error ? 'error' : 'info'"
                    @blur="v$.display_name.touch"
                  />
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label
                  class="text-sm font-medium whitespace-nowrap text-slate-900 dark:text-slate-50 text-wrap"
                >
                  {{ t('WGPT_SAML.PROVIDERS.EDIT.FORM.IDENTIFIER.LABEL') }}
                </label>
                <div class="relative flex flex-col min-w-0 gap-1">
                  <Input v-model="state.identifier" type="text" readonly />
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <label
                  class="text-sm font-medium whitespace-nowrap text-slate-900 dark:text-slate-50 text-wrap"
                >
                  {{ t('WGPT_SAML.PROVIDERS.EDIT.FORM.SP_METADATA.LABEL') }}
                </label>
                <div
                  class="relative flex flex-col min-w-0 gap-1"
                  :class="{ error: v$.sp_metadata.$error }"
                >
                  <TextArea
                    v-model.trim="state.sp_metadata"
                    :message="
                      v$.sp_metadata.$error
                        ? t('WGPT_SAML.PROVIDERS.EDIT.FORM.SP_METADATA.ERROR')
                        : ''
                    "
                    :message-type="v$.sp_metadata.$error ? 'error' : 'info'"
                    @blur="v$.sp_metadata.touch"
                  />
                </div>
              </div>
            </div>

            <div class="py-8 flex flex-col items-start w-full gap-2">
              <Button
                :label="t('WGPT_SAML.PROVIDERS.EDIT.DOWNLOAD.METADATA')"
                :disabled="!props.samlProvider.idp_metadata"
                ghost
                xs
                blue
                icon="i-lucide-download"
                class="-ms-2"
                @click="onDownloadMetadata"
              />
              <Button
                :label="
                  t('WGPT_SAML.PROVIDERS.EDIT.DOWNLOAD.SIGNING_CERTIFICATE')
                "
                :disabled="!props.samlProvider.idp_signing_cert"
                ghost
                xs
                blue
                icon="i-lucide-download"
                class="-ms-2"
                @click="onDownloadSigningCertificate"
              />
              <Button
                :label="
                  t('WGPT_SAML.PROVIDERS.EDIT.DOWNLOAD.ENCRYPTION_CERTIFICATE')
                "
                :disabled="!props.samlProvider.idp_encryption_cert"
                ghost
                xs
                blue
                icon="i-lucide-download"
                class="-ms-2"
                @click="onDownloadEncryptionCertificate"
              />
            </div>

            <div class="py-8 flex w-full gap-2">
              <Button
                :label="t('WGPT_SAML.PROVIDERS.EDIT.FORM.UPDATE')"
                :disabled="uiFlags.isUpdating"
                :is-loading="uiFlags.isUpdating"
                @click="onSubmit"
              />
            </div>
          </SettingsSection>
        </div>
      </div>
    </div>
  </div>
</template>
